import { lazy, Suspense } from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import NotFound from '@/components/route/NotFound';
import {
  CALENDAR,
  CANDIDATES,
  HIRING_APPROVALS,
  COMPANIES,
  DASHBOARD,
  INVITE,
  JOBS,
  LOGIN,
  MESSAGES,
  ONBOARD,
  TERMS,
  RECRUITERS,
  RESET_PASSWORD,
  GOOGLE_REDIRECT_HANDLER,
  PROFILE_SETTINGS,
  CANDIDATES_SPECIFICATION_SETTINGS,
  RECRUITER_MANAGEMENT,
  JOB_ASSIGNMENTS,
  NOTIFICATIONS_SETTINGS,
  NOTIFICATIONS,
  GUEST,
  PRIVACY_POLICY,
  RATE,
  REGISTER,
  MAGIC_LINK_INIT,
  MAGIC_LINK,
  RECRUITER,
  CLIENT,
  ATS_SETTINGS,
  ATS_DEFAULT_MAPPINGS,
  ANALYTICS,
  MASTER_CONTRACTS,
  RECRUITER_CONTRACTS,
  EMBED,
  RECRUITER_COMPANY_SETTINGS,
  RECRUITER_COMPANY_TYPE_SETTINGS,
  RECRUITER_COMPANY_USERS_SETTINGS,
  RECRUITER_PROJECTS,
  PERMISSIONS_SETTINGS,
  MFA_SETUP,
} from '@/constants/routes';
import useUrlLanguageDetection from '@/hooks/useUrlLanguageDetection';
// ─── Pages kept eager (public / auth critical path, small) ───────────────────
import GoogleRedirectHandler from '@/pages/GoogleRedirectHandler';
import Guest from '@/pages/Guest';
import Home from '@/pages/Home';
import InvitationConfirm from '@/pages/InvitationConfirm';
import Login from '@/pages/Login';
import MagicLink from '@/pages/MagicLink';
import MagicLinkInit from '@/pages/MagicLinkInit';
import Onboard from '@/pages/Onboard';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Rate from '@/pages/Rate';
import ResetPassword from '@/pages/ResetPassword';
import Terms from '@/pages/Terms';

import AdminRoute from './AdminRoute';
import AppErrorBoundary from './AppErrorBoundary';
import EmbedThemeLayout from './EmbedThemeLayout';
import GuestRoute from './GuestRoute';
import NotificationRoute from './NotificationRoute';
import { PageFallback } from './PageFallback';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import RouterErrorPage from './RouterErrorPage';

// ─── Core private pages ───────────────────────────────────────────────────────
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Jobs = lazy(() => import('@/features/jobs/JobsPage'));
const SingleJob = lazy(() => import('@/features/jobs/SingleJobPage'));
const Candidates = lazy(() => import('@/features/candidates/CandidatesPage'));
const Messages = lazy(() => import('@/features/messages/MessagesPage'));
const Calendar = lazy(() => import('@/features/calendar/CalendarPage'));
const Recruiters = lazy(() => import('@/features/recruiters/RecruitersPage'));
const Clients = lazy(() => import('@/features/clients/ClientsPage'));
const HiringApprovals = lazy(
  () => import('@/features/invoicing/InvoicingPage'),
);
const RecruiterContracts = lazy(
  () => import('@/features/recruiterContracts/RecruiterContractsPage'),
);

// ─── Analytics (heavy: CubeJS + chart.js) ────────────────────────────────────
const Analytics = lazy(() => import('@/features/analytics/AnalyticsPage'));
// ─── ATS settings (contains @hello-pangea/dnd) ───────────────────────────────
const AtsSettingsPage = lazy(
  () => import('@/features/atsSettings/AtsSettings'),
);
const AtsJobSettings = lazy(
  () => import('@/features/atsSettings/AtsJobSettings'),
);
const DefaultStageMappings = lazy(
  () => import('@/features/atsSettings/DefaultStageMappings'),
);

// ─── Recruiter management (heavy: 1183-line Marketplace tree) ────────────────
const RecruiterManagementPage = lazy(
  () => import('@/features/recruiterManagement/RecruiterManagementPage'),
);
const RecruiterPublicProfile = lazy(
  () => import('@/pages/RecruiterPublicProfile'),
);
const ClientPublicProfile = lazy(() => import('@/pages/ClientPublicProfile'));
const MasterContracts = lazy(
  () => import('@/features/settings/MasterContractsPage'),
);
const EditMasterContract = lazy(
  () => import('@/features/settings/EditMasterContractPage'),
);

// ─── Settings (rarely visited on first session) ──────────────────────────────
const ProfileSettings = lazy(
  () => import('@/features/profile/ProfileSettingsPage'),
);
const PermissionsSettings = lazy(
  () => import('@/features/permissions/PermissionsSettingsPage'),
);
const NotificationsSettings = lazy(
  () => import('@/features/settings/NotificationsSettingsPage'),
);
const CandidatesSpecificationSettings = lazy(
  () => import('@/features/settings/CandidatesSpecificationSettingsPage'),
);
const RecruiterCompanySettings = lazy(
  () => import('@/features/settings/RecruiterCompanySettingsPage'),
);
const RecruiterProjectsPage = lazy(
  () => import('@/features/recruiterProjects/RecruiterProjectsPage'),
);
const RecruiterCompanyTypeSettings = lazy(
  () => import('@/features/settings/RecruiterCompanyTypeSettingsPage'),
);
const MFASetupPage = lazy(() => import('@/pages/MFASetup'));

// ─── Job edit wizard (heavy forms, tiptap) ───────────────────────────────────
const Job = lazy(() => import('@/features/jobs/JobDraftRedirectPage'));
const JobSteps = lazy(() => import('@/features/jobs/JobStepsPage'));

// ─── Embed entry points ──────────────────────────────────────────────────────
const EmbedRoutes = lazy(() => import('@/pages/embed/routes'));

const PrototypeRoutes = import.meta.env.DEV
  ? lazy(() => import('@/prototypes/PrototypeRoutes'))
  : null;

/** Strip the leading slash so route constants can be used as relative child paths. */
const rel = (path: string) => path.replace(/^\//, '');

const RootLayout = () => {
  useUrlLanguageDetection();
  return <Outlet />;
};

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouterErrorPage />,
    children: [
      {
        element: <PublicRoute />,
        children: [
          { index: true, element: <Home /> },
          {
            path: rel(RESET_PASSWORD) + '/:uid/:token',
            element: <ResetPassword />,
          },
          {
            path: rel(INVITE) + '/:uid/:token',
            element: <InvitationConfirm />,
          },
          {
            path: rel(GOOGLE_REDIRECT_HANDLER),
            element: <GoogleRedirectHandler />,
          },
          { path: rel(LOGIN), element: <Login /> },
          { path: rel(REGISTER), element: <Navigate to={LOGIN} replace /> },
        ],
      },
      {
        element: <PrivateRoute />,
        children: [
          { path: rel(ONBOARD), element: <Onboard /> },
          { path: rel(TERMS), element: <Terms /> },
          { path: rel(DASHBOARD), element: <Dashboard /> },
          { path: rel(JOBS), element: <Jobs /> },
          {
            path: rel(RECRUITER) + '/:recruiterId',
            element: <RecruiterPublicProfile />,
          },
          {
            path:
              rel(RECRUITER) +
              '/:recruiterId/edit/:recruiterCompanyAgreementId',
            element: <RecruiterPublicProfile />,
          },
          {
            path: rel(RECRUITER_MANAGEMENT),
            element: <RecruiterManagementPage />,
          },
          {
            path: rel(JOB_ASSIGNMENTS),
            element: <RecruiterManagementPage />,
          },
          { path: rel(ANALYTICS), element: <Analytics /> },
          { path: rel(MASTER_CONTRACTS), element: <MasterContracts /> },
          {
            path: rel(MASTER_CONTRACTS) + '/:id',
            element: <EditMasterContract />,
          },
          { path: rel(RECRUITER_CONTRACTS), element: <RecruiterContracts /> },
          {
            path: rel(CLIENT) + '/:clientId',
            element: <ClientPublicProfile />,
          },
          { path: rel(JOBS) + '/:id', element: <SingleJob isPublic={false} /> },
          {
            path: rel(JOBS) + '/:id/:candidate',
            element: <SingleJob isPublic={false} />,
          },
          { path: rel(JOBS) + '/edit/:id', element: <Job /> },
          { path: rel(JOBS) + '/edit/:id/:step', element: <JobSteps /> },
          { path: rel(CANDIDATES), element: <Candidates /> },
          { path: rel(HIRING_APPROVALS), element: <HiringApprovals /> },
          {
            element: <AdminRoute />,
            children: [
              { path: rel(RECRUITERS), element: <Recruiters /> },
              { path: rel(COMPANIES), element: <Clients /> },
            ],
          },
          { path: rel(MESSAGES), element: <Messages /> },
          { path: rel(MESSAGES) + '/:contactId', element: <Messages /> },
          {
            path: rel(MESSAGES) + '/:contactId/:interviewId',
            element: <Messages />,
          },
          { path: rel(CALENDAR), element: <Calendar /> },
          { path: rel(PROFILE_SETTINGS), element: <ProfileSettings /> },
          {
            path: rel(CANDIDATES_SPECIFICATION_SETTINGS),
            element: <CandidatesSpecificationSettings />,
          },
          {
            path: rel(RECRUITER_COMPANY_SETTINGS),
            element: <RecruiterCompanySettings />,
          },
          {
            path: rel(RECRUITER_PROJECTS),
            element: <RecruiterProjectsPage />,
          },
          {
            path: rel(RECRUITER_COMPANY_TYPE_SETTINGS),
            element: <RecruiterCompanyTypeSettings />,
          },
          {
            path: rel(RECRUITER_COMPANY_USERS_SETTINGS),
            element: <Navigate to={RECRUITER_COMPANY_SETTINGS} replace />,
          },
          { path: rel(ATS_SETTINGS), element: <AtsSettingsPage /> },
          {
            path: rel(ATS_DEFAULT_MAPPINGS),
            element: <DefaultStageMappings />,
          },
          {
            path: `${rel(ATS_SETTINGS)}${JOBS}/:id`,
            element: <AtsJobSettings />,
          },
          {
            path: rel(NOTIFICATIONS_SETTINGS),
            element: <NotificationsSettings />,
          },
          { path: rel(PERMISSIONS_SETTINGS), element: <PermissionsSettings /> },
          { path: rel(MFA_SETUP), element: <MFASetupPage /> },
          { path: rel(RATE) + '/:interviewId', element: <Rate /> },
        ],
      },
      {
        element: <NotificationRoute />,
        children: [{ path: rel(NOTIFICATIONS), element: <Login /> }],
      },
      {
        element: <GuestRoute />,
        children: [{ path: rel(GUEST), element: <Guest /> }],
      },
      { path: rel(PRIVACY_POLICY), element: <PrivacyPolicy /> },
      {
        path: 'public' + JOBS + '/:id',
        element: <SingleJob isPublic showBackButton={false} />,
      },
      { path: rel(MAGIC_LINK_INIT) + '/*', element: <MagicLinkInit /> },
      { path: rel(MAGIC_LINK), element: <MagicLink /> },
      {
        path: rel(EMBED),
        element: <EmbedThemeLayout />,
        children: [{ path: '*', element: <EmbedRoutes /> }],
      },
      ...(import.meta.env.DEV && PrototypeRoutes
        ? [{ path: 'prototypes/*', element: <PrototypeRoutes /> }]
        : []),
      { path: '*', element: <NotFound /> },
    ],
  },
]);

const Routes = () => {
  return (
    <div>
      <AppErrorBoundary>
        <Suspense fallback={<PageFallback />}>
          <RouterProvider router={router} />
        </Suspense>
      </AppErrorBoundary>
    </div>
  );
};

export default Routes;
